import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import { APIResponse } from 'src/services/base.api.service';
import { errorMessages } from 'src/constants';
import { v4 as uuidv4 } from 'uuid';
import { AppDispatch } from '../store';
import { openPopup, startLoading, stopLoading } from '../common/common.slice';
import { VentureAPIService } from 'src/services/venture.service';
import { TCreateCommentRequest, TCreateCommentResponse, TDeleteCommentRequest, TDeleteCommentResponse, TDeleteReplyToCommentRequest, TDeleteReplyToCommentResponse, TEditCommentRequest, TEditCommentResponse, TEditReplyToCommentRequest, TGetCommentsRequest, TGetCommentsResponse, TGetRepliesRequest, TLikeCommentRequest, TLikeCommentResponse, TLikeReplyRequest, TReplyToCommentRequest, TReplyToCommentResponse, TUnLikeCommentRequest, TUnLikeReplyRequest } from 'src/services/apiEndpoint.types.ts';
import { TComment } from 'src/components/common';
import { TGetVentureRepliesRequest } from 'src/services/apiEndpoint.types.ts/socialMedia/getReplies.endpoint.types';
import { AnyAsyncThunk } from '@reduxjs/toolkit/dist/matchers';
import { DemoDayBooking, Payment, RazorpayOptions, TGlobalDemoDay, VerifyPayment } from './venture.types';
import { TCofounderRequest, TSingleVentureRequest, TVenture, TVentureCounter, TVentureRequest } from 'src/services/apiEndpoint.types.ts/ventures/ventures.types';
type TVentureInitialState = {
  clickFocusPoint: string;
  ventures: any;
  loading: boolean;
  ventureLoading: boolean;
  moreVenturesLeft: boolean;
  venturesPageNumber: number;
  venturesPerPage: number;
  singleVenture: null | any;
  myVenture: any;
  counters: {
    comments: number,
    hiringTeams: number,
    seekingCoFounders: number,
    ventures: number
  },
  createCommentLoader: boolean,
  editCommentLoader: boolean,
  /*
     ** Comments
     */
  comments: any[];
  commentsPerPage: number;
  commentsPageNumber: number;
  MoreCommentsLeft: boolean;
  showCommentsId: string;
  commentsLoading: boolean;

  /*
     ** Replies
     */
  replyToCommentLoader: boolean
  replies: any[];
  repliesPerPage: number;
  repliesPageNumber: number;
  moreRepliesLeft: boolean;
  showRepliesId: string;
  repliesLoading: boolean;

  editReplyToCommentLoader: boolean;

  demoDay: any;
  globalDemoDay: TGlobalDemoDay[]
};

const initialState: TVentureInitialState = {
  loading: false,
  clickFocusPoint: '',
  ventureLoading: false,
  ventures: [],
  moreVenturesLeft: false,
  venturesPageNumber: 1,
  venturesPerPage: 50,
  singleVenture: null,
  myVenture: null,
  counters: {
    comments: 0,
    hiringTeams: 0,
    seekingCoFounders: 0,
    ventures: 0
  },
  createCommentLoader: false,
  editCommentLoader: false,
  /*
   ** Comments
   */
  comments: [],
  commentsPerPage: 5,
  commentsPageNumber: 1,
  MoreCommentsLeft: true,
  showCommentsId: '',
  commentsLoading: false,
  /*
   ** Replies
   */
  replyToCommentLoader: false,
  replies: [],
  repliesPerPage: 5,
  repliesPageNumber: 1,
  moreRepliesLeft: false,
  showRepliesId: '',
  repliesLoading: false,
  editReplyToCommentLoader: false,

  // demo day

  demoDay: null,
  globalDemoDay: []
};

export const ventureSlice = createSlice({
  name: 'venture',
  initialState,
  reducers: {
    setLoading: (state, action) => {
      state.loading = action.payload
    },
    setFocusClickPoint: (state, action) => {
      state.clickFocusPoint = action.payload
    },
    clearFocusPoint: (state) => {
      state.clickFocusPoint = ''
    },
    startVentureLoading: (state) => {
      state.ventureLoading = true
    },
    endVentureLoading: (state) => {
      state.ventureLoading = false
    },
    ventureListSuccess: (state, action) => {
      let curentVentures = JSON.parse(JSON.stringify(state?.ventures)) || [];

      const { ventureVerses, currentPage, itemsPerPage, totalPages } = action.payload;

      if (currentPage === 1) {
        curentVentures = ventureVerses;
      } else {
        ventureVerses.forEach((venture) => {
          const id = venture?.id;

          const indexInExistingVentures = curentVentures.findIndex(
            (venture) => venture?.id === id
          );

          if (indexInExistingVentures < 0) {
            curentVentures.push(venture);
          }
        });
      }

      state.ventures = curentVentures;
      state.moreVenturesLeft = currentPage < totalPages;
      state.venturesPageNumber = currentPage;
      state.venturesPerPage = itemsPerPage;
    },
    clearVentureList: (state) => {
      state.ventures = []
    },
    resetVentureList: (state) => {
      state.ventures = [];
      state.moreVenturesLeft = false;
      state.venturesPageNumber = 1;
      state.venturesPerPage = 50;
    },
    increamentVenturePage: (state, action) => {
      state.venturesPageNumber = state.venturesPageNumber + action.payload
    },
    singleVentureSuccess: (state, action) => {
      state.singleVenture = action.payload
    },
    clearSingleVenture: (state) => {
      state.singleVenture = null
    },
    clearMyVenture: (state) => {
      state.myVenture = null
    },
    myVentureSuccess: (state, action) => {
      state.myVenture = action.payload
    },
    ventureVerseCounterSuccess: (state, action) => {
      state.counters = action.payload
    },
    updateCreateCommentLoader: (state, action: PayloadAction<boolean>) => {
      state.createCommentLoader = action.payload;
    },
    updateEditCommentLoader: (state, action: PayloadAction<boolean>) => {
      state.editCommentLoader = action.payload;
    },
    createCommentSuccess: (state, action: PayloadAction<any>) => {
      const currentComments = JSON.parse(JSON.stringify(state.comments));
      const newComments = action.payload.comments
      newComments.forEach((item) => {
        const itemId = item.id;

        const indexInExistingFeed = currentComments.findIndex(
          (item) => item.id === itemId
        );

        if (indexInExistingFeed < 0) {
          currentComments.unshift(item);
        }
      });
      state.comments = currentComments;
    },
    getCommentsSuccess: (state, action: PayloadAction<any>) => {
      let currentComments: TComment[] = JSON.parse(JSON.stringify(state.comments));
      let { comments: newComments, totalPages, currentPage, prevPage }: { comments: any[]; totalPages: number, currentPage: number, prevPage: number } =
        action.payload;
      // if (state.showCommentsId !== action.payload.postId) {
      //   currentComments = []
      // }
      if (currentComments?.length === 0) {
        newComments = newComments.reverse()
      }
      if (currentPage === prevPage) {
        newComments.forEach((item) => {
          const itemId = item.id;

          const indexInExistingFeed = currentComments.findIndex(
            (item) => item.id === itemId
          );

          if (indexInExistingFeed < 0) {
            currentComments.unshift(item);
          }
        });
      } else {
        function mergeAndRemoveDuplicates(array1, array2) {
          const mergedArray = [...array1, ...array2];

          // Remove duplicates based on `comments.id`
          const uniqueArray = mergedArray.reduce((acc, current) => {
            const duplicate = acc.find(item => item.comments.id === current.comments.id);
            if (!duplicate) {
              acc.push(current); // Add if no duplicate exists
            }
            return acc;
          }, []);

          return uniqueArray;
        }
        currentComments = mergeAndRemoveDuplicates(currentComments, newComments)
      }
      state.comments = currentComments;
      state.MoreCommentsLeft = state.commentsPageNumber < totalPages;
    },
    incrementCommentsPage: (state, action: PayloadAction<number>) => {
      state.commentsPageNumber = state.commentsPageNumber + action.payload;
    },
    startCommentsLoader: (state) => {
      state.commentsLoading = true;
    },
    stopCommentsLoader: (state) => {
      state.commentsLoading = false;
    },
    /*
     ** clear comments
     */
    clearComments: (state) => {
      state.comments = [];
      state.commentsPageNumber = 1;
      state.MoreCommentsLeft = false;
      state.showCommentsId = '';
      state.replies = [];
      state.repliesPageNumber = 1;
      state.moreRepliesLeft = false;
      state.showRepliesId = '';
    },
    likeCommentSuccess: (state, action: PayloadAction<any>) => {
      // increasing the like count of comments in the local state
      const commentId: string = action.payload;

      const currentComments: any = JSON.parse(JSON.stringify(state.comments));

      const currentComment = currentComments.find((comment) => comment?.id === commentId);
      if (currentComment) {
        currentComment.likesCount++;
        currentComment.likedByUser = true;
      }

      state.comments = currentComments;
    },
    /*
     ** like post success - When a post is liked successfully
     */
    unlikeCommentSuccess: (state, action: PayloadAction<any>) => {
      // decreasing the like count of comments in the local state
      const commentId: string = action.payload;

      const currentComments: any = JSON.parse(JSON.stringify(state.comments));

      const currentComment = currentComments.find((comment) => comment?.id === commentId);
      if (currentComment) {
        currentComment.likesCount--;
        currentComment.likedByUser = false;
      }

      state.comments = currentComments;
    },
    startRepliesLoader: (state) => {
      state.repliesLoading = true;
    },
    stopRepliesLoader: (state) => {
      state.repliesLoading = false;
    },
    /*
         ** Which reply section is open.
         */
    updateShowRepliesId: (state, action: PayloadAction<string>) => {
      state.showRepliesId = action.payload;
      state.replies = [];
      state.repliesPageNumber = 1;
      state.moreRepliesLeft = false;
    },
    getRepliesSuccess: (state, action: PayloadAction<any>) => {
      const currentReplies: any = state?.repliesPageNumber > 1 ? JSON.parse(JSON.stringify(state.replies)) : []

      const { replies, totalPages }: { replies: any; totalPages: number } =
        action.payload;

      replies.forEach((item) => {
        const itemId = item?.id;

        const indexInExistingFeed = currentReplies.findIndex((item) => item.id === itemId);

        if (indexInExistingFeed < 0) {
          currentReplies.unshift(item);
        }
      });

      state.replies = currentReplies;
      state.moreRepliesLeft = state.repliesPageNumber < totalPages;
    },
    clearRepliesSuccess: (state) => {
      state.replies = [];
      state.moreRepliesLeft = false
      state.repliesPageNumber = 1;
    },
    replyToCommentSuccess: (state, action: PayloadAction<any>) => {
      // increasing the comment in the local state
      const commentId: string = action.payload.commentId;
      const newReplies = action.payload.replies;
      const currentReplies = JSON.parse(JSON.stringify(state.replies))
      const currentComments: any = JSON.parse(JSON.stringify(state.comments));

      newReplies.forEach(reply => {
        const existReplyIndex = currentReplies.findIndex((item) => item.id === reply.id)
        if (existReplyIndex < 0) {
          currentReplies.push(reply)
        }
      });

      const currentComment = currentComments.find((comment) => comment?.id === commentId);

      if (currentComment) {
        currentComment.replyCount++;
      }

      state.comments = currentComments;
      state.replies = currentReplies
    },
    /*
     ** edit comment success - When a comment is created successfully
     */
    editCommentSuccess: (state) => {
      state.commentsPageNumber = 1;
      state.comments = [];
    },
    /*
        ** delete comment success - When a comment is deleted successfully
        */
    deleteCommentSuccess: (state, action: PayloadAction<TDeleteCommentRequest>) => {
      const { commentId } = action.payload;
      const newComments: TComment[] = JSON.parse(JSON.stringify(state.comments));
      const deletedCommentIndex: number = newComments.findIndex(
        (comment) => comment?.id === commentId
      );

      if (deletedCommentIndex > -1) {
        newComments.splice(deletedCommentIndex, 1);
      }
      state.comments = newComments;
    },
    /*
     ** Update page number of replies feed
     */
    updateReplyToCommentLoader: (state, action: PayloadAction<boolean>) => {
      state.replyToCommentLoader = action.payload;
    },
    incrementRepliesPage: (state, action: PayloadAction<number>) => {
      state.repliesPageNumber = state.repliesPageNumber + action.payload;
    },
    /*
         ** clear replies
         */
    clearReplies: (state) => {
      state.replies = [];
      state.repliesPageNumber = 1;
      state.moreRepliesLeft = true;
      state.showRepliesId = '';
    },
    /*
         ** like reply success - When a reply is liked successfully
         */
    likeReplySuccess: (state, action) => {
      // increasing the like count of comments in the local state
      const { replyId } = action.payload;

      const currentReplies: any = JSON.parse(JSON.stringify(state.replies));
      const currentReply = currentReplies.find((reply) => reply.id === replyId);

      if (currentReply) {
        currentReply.likesCount++;
        currentReply.likedByUser = true;
      }

      state.replies = currentReplies;
    },
    /*
     ** unlike reply success - When a reply is unliked successfully
     */
    unlikeReplySuccess: (state, action) => {
      // decreasing the like count of comments in the local state
      const { replyId } = action.payload;

      const currentReplies: any = JSON.parse(JSON.stringify(state.replies));

      const currentReply = currentReplies.find((reply) => reply?.id === replyId);

      // if (currentPost) {
      //   currentPost.post.likeCount--;
      // }

      if (currentReply) {
        currentReply.likesCount--;
        currentReply.likedByUser = false;
      }

      state.replies = currentReplies;
    },
    /*
         ** delete reply success - When a reply is deleted successfully
         */
    deleteReplySuccess: (state, action: PayloadAction<TDeleteReplyToCommentRequest>) => {
      const { replyId, commentId } = action.payload;

      const newComments: any | never[] = JSON.parse(JSON.stringify(state.comments)) || [];
      const modifiedComment: any = newComments.find(
        (comment: any) => comment?.id === commentId
      );

      const newReplies: any = JSON.parse(JSON.stringify(state.replies));
      const deletedReplyIndex: number = newReplies.findIndex(
        (reply) => reply?.id === replyId
      );

      if (modifiedComment) {
        modifiedComment.replyCount--;
      }

      if (deletedReplyIndex > -1) {
        newReplies.splice(deletedReplyIndex, 1);
      }
      state.comments = newComments;
      state.replies = newReplies;
    },
    /*
         ** edit reply success - When a reply is edited successfully
         */
    editReplyToCommentSuccess: (
      state,
      action: PayloadAction<{ replyId: string; text: string }>
    ) => {
      const { replyId, text } = action.payload;

      const newReplies: any = JSON.parse(JSON.stringify(state.replies));

      const editedReply: any = newReplies.find(
        (reply) => reply?.id === replyId
      );

      if (editedReply) {
        editedReply.content = text;
      }

      state.replies = newReplies;
    },
    updateEditReplyToCommentLoader: (state, action: PayloadAction<boolean>) => {
      state.editReplyToCommentLoader = action.payload;
    },
    clearCounter: (state) => {
      state.counters = {
        comments: 0,
        hiringTeams: 0,
        seekingCoFounders: 0,
        ventures: 0
      }
    },
    demoDaySuccess: (state, action) => {
      state.demoDay = action.payload
    },
    clearDemoDay: (state) => {
      state.demoDay = null
    },
    globalDemoDaySuccess: (state, action) => {
      state.globalDemoDay = action.payload
    }
  }
});

// Action creators are generated for each case reducer function
export const {
  setLoading,
  setFocusClickPoint,
  clearFocusPoint,
  startVentureLoading,
  endVentureLoading,
  ventureListSuccess,
  resetVentureList,
  increamentVenturePage,
  singleVentureSuccess,
  myVentureSuccess,
  clearVentureList,
  clearSingleVenture,
  clearMyVenture,
  ventureVerseCounterSuccess,
  updateCreateCommentLoader,
  updateEditCommentLoader,
  createCommentSuccess,
  editCommentSuccess,
  deleteCommentSuccess,
  getCommentsSuccess,
  incrementCommentsPage,
  startCommentsLoader,
  stopCommentsLoader,
  likeCommentSuccess,
  unlikeCommentSuccess,
  startRepliesLoader,
  stopRepliesLoader,
  updateShowRepliesId,
  getRepliesSuccess,
  clearRepliesSuccess,
  clearReplies,
  incrementRepliesPage,
  updateReplyToCommentLoader,
  replyToCommentSuccess,
  clearComments,
  likeReplySuccess,
  unlikeReplySuccess,
  deleteReplySuccess,
  editReplyToCommentSuccess,
  updateEditReplyToCommentLoader,
  demoDaySuccess,
  clearDemoDay,
  globalDemoDaySuccess
} = ventureSlice.actions;

export default ventureSlice.reducer;

const errorHandler = (err: AxiosError) => (dispatch: AppDispatch) => {
  const errorResponse = (err as unknown as AxiosError)?.response;
  const message = (errorResponse?.data as { message: string })?.message || err?.message;
  dispatch(
    openPopup({
      popupMessage:
        errorMessages[message as keyof typeof errorMessages] || errorMessages.unknownError,
      popupType: 'error'
    })
  );
};

export const createVenture = (payload: TVenture) => async (dispatch: AppDispatch) => {
  try {
    dispatch(startLoading());
    const response = await new VentureAPIService().createVenture(payload);

    // Ensure correct response structure
    if (response?.data) {
      return {
        status: true,
        data: response.data // Return actual API response data
      };
    } else {
      throw new Error('Invalid response from server');
    }
  } catch (err) {
    const message = err?.response?.data?.message || err?.message || 'Something went wrong';

    dispatch(
      openPopup({
        popupMessage: message,
        popupType: 'error'
      })
    );

    return {
      data: {
        status: false,
        error: message
      }
    };
  } finally {
    dispatch(stopLoading());
  }
};
export const updateVenture = (payload: TVenture) => async (dispatch: AppDispatch) => {
  try {
    dispatch(startLoading());
    const response = await new VentureAPIService().updateVenture(payload);

    // Ensure correct response structure
    if (response?.data) {
      return {
        status: true,
        data: response.data // Return actual API response data
      };
    } else {
      throw new Error('Invalid response from server');
    }
  } catch (err) {
    const message = err?.response?.data?.message || err?.message || 'Something went wrong';

    dispatch(
      openPopup({
        popupMessage: message,
        popupType: 'error'
      })
    );

    return {
      data: {
        status: false,
        error: message
      }
    };
  } finally {
    dispatch(stopLoading());
  }
};

export const getCoFounder = (payload: TCofounderRequest) => async (dispatch: AppDispatch) => {
  try {
    const response = await new VentureAPIService().getCoFounder(payload);

    // Ensure correct response structure
    if (response?.data) {
      return {
        status: true,
        data: response.data // Return actual API response data
      };
    } else {
      throw new Error('Invalid response from server');
    }
  } catch (err) {
    const message = err?.response?.data?.message || err?.message || 'Something went wrong';

    dispatch(
      openPopup({
        popupMessage: message,
        popupType: 'error'
      })
    );

    return {
      data: {
        status: false,
        error: message
      }
    };
  } finally {
    dispatch(stopLoading());
  }
};
export const getVentureList = (payload: TVentureRequest) => async (dispatch: AppDispatch) => {
  dispatch(startLoading())
  try {
    const { status, data }: { status: number, data: any } = await new VentureAPIService().getVentureList(payload);
    if (status === 200) {
      dispatch(ventureListSuccess(data.data))
    }
  } catch (err) {
    console.log(err, 'error');
  } finally {
    dispatch(stopLoading());
  }
};

export const getSingleVenture = (payload: TSingleVentureRequest) => async (dispatch: AppDispatch) => {
  dispatch(startVentureLoading())
  try {
    const { status, data }: { status: number, data: any } = await new VentureAPIService().getSingleVenture(payload);
    if (status === 200) {
      dispatch(singleVentureSuccess(data.data))
    }
  } catch (err) {
    console.log(err, 'error');
  } finally {
    dispatch(endVentureLoading())
  }
};

export const getSpecificVenture = (payload: any) => async (dispatch: AppDispatch) => {
  dispatch(startVentureLoading())
  try {
    const { status, data }: { status: number, data: any } = await new VentureAPIService().getVentureList(payload);
    if (status === 200) {
      dispatch(singleVentureSuccess(data.data))
    }
  } catch (err) {
    console.log(err, 'error');
  } finally {
    dispatch(endVentureLoading())
  }
};

export const getMyVenture = (payload: TSingleVentureRequest) => async (dispatch: AppDispatch) => {
  try {
    const { data, status }: { status: number, data: any } = await new VentureAPIService().getVentureList(payload);
    // Ensure correct response structure
    if (status === 200) {
      dispatch(myVentureSuccess(data.data.ventureVerses?.[0] || null))
    }
  } catch (err) {
    const message = err?.response?.data?.message || err?.message || 'Something went wrong';
    return {
      data: {
        status: false,
        error: message
      }
    };
  }
};

export const getVentureCounters = (payload: TSingleVentureRequest) => async (dispatch: AppDispatch) => {
  dispatch(setLoading(true))
  try {
    const { data, status }: { status: number, data: TVentureCounter } = await new VentureAPIService().getVentureCounter(payload);
    // Ensure correct response structure
    if (status === 200) {
      dispatch(ventureVerseCounterSuccess(data.count))
      dispatch(myVentureSuccess(data?.count?.myVenture))
    }
  } catch (err) {
    console.log(err, 'err');
  } finally {
    dispatch(setLoading(false))
  }
};

export const ventureVerseLike = (payload: any) => async (dispatch: AppDispatch) => {
  try {
    const response = await new VentureAPIService().likeVentureVerse(payload);

    // Ensure correct response structure
    if (response?.data) {
      return {
        status: true,
        data: response.data // Return actual API response data
      };
    } else {
      throw new Error('Invalid response from server');
    }
  } catch (err) {
    const message = err?.response?.data?.message || err?.message || 'Something went wrong';

    dispatch(
      openPopup({
        popupMessage: message,
        popupType: 'error'
      })
    );

    return {
      data: {
        status: false,
        error: message
      }
    };
  } finally {
    dispatch(stopLoading());
  }
};

export const ventureVerseUnLike = (payload: any) => async (dispatch: AppDispatch) => {
  try {
    const response = await new VentureAPIService().unlikeVentureVerse(payload);

    // Ensure correct response structure
    if (response?.data) {
      return {
        status: true,
        data: response.data // Return actual API response data
      };
    } else {
      throw new Error('Invalid response from server');
    }
  } catch (err) {
    const message = err?.response?.data?.message || err?.message || 'Something went wrong';

    dispatch(
      openPopup({
        popupMessage: message,
        popupType: 'error'
      })
    );

    return {
      data: {
        status: false,
        error: message
      }
    };
  } finally {
    dispatch(stopLoading());
  }
};

export const createComment = (payload: TCreateCommentRequest) => async (dispatch: AppDispatch) => {
  try {
    dispatch(updateCreateCommentLoader(true));
    const { status }: APIResponse<TCreateCommentResponse> =
      await new VentureAPIService().createComment(payload);

    if (status === 200) {
      const data: TGetCommentsRequest = {
        ...(payload.userId ? { userId: payload.userId } : {}),
        // userType: payload.userType,
        page: 1,
        limit: 5,
        ventureVerseId: payload.ventureVerseId
      };
      const response: APIResponse<TGetCommentsResponse> =
        await new VentureAPIService().getComments(data);
      let comments = []
      if (response.status === 200) {
        comments = response.data.data.comments
      }
      dispatch(createCommentSuccess({ comments }));
      dispatch(updateCreateCommentLoader(false));
    }
  } catch (err) {
    console.log(err, 'commentserror');
    dispatch(errorHandler(err as unknown as AxiosError));
  } finally {
    dispatch(updateCreateCommentLoader(false));
  }
};
export const editComment = (payload: TEditCommentRequest) => async (dispatch: AppDispatch) => {
  try {
    dispatch(updateEditCommentLoader(true));
    const { status }: APIResponse<TEditCommentResponse> =
      await new VentureAPIService().editComment(payload);

    if (status === 200) {
      dispatch(editCommentSuccess());
    }
  } catch (err) {
    dispatch(errorHandler(err as unknown as AxiosError));
  } finally {
    dispatch(updateEditCommentLoader(false));
  }
};
export const getComments = (payload: TGetCommentsRequest) => async (dispatch: AppDispatch) => {
  const { prevPage = 1, ...payloadData } = payload
  try {
    dispatch(startCommentsLoader());
    const { status, data }: APIResponse<TGetCommentsResponse> =
      await new VentureAPIService().getComments(payloadData);

    if (status === 200) {
      dispatch(getCommentsSuccess({ ...data?.data, prevPage }));
    }
  } catch (err) {
    dispatch(errorHandler(err as unknown as AxiosError));
  } finally {
    dispatch(stopCommentsLoader());
  }
};
export const deleteComment = (payload: TDeleteCommentRequest) => async (dispatch: AppDispatch) => {
  try {
    const { status }: any =
      await new VentureAPIService().deleteComment(payload);

    if (status === 200) {
      dispatch(deleteCommentSuccess({ commentId: payload.commentId }));
    }
  } catch (err) {
    dispatch(errorHandler(err as unknown as AxiosError));
  }
};

export const likeComment = (payload: TLikeCommentRequest) => async (dispatch: AppDispatch) => {
  try {
    const response: any =
      await new VentureAPIService().likeVentureVerse(payload);
    if (response.status === 200) {
      dispatch(likeCommentSuccess(payload.entityId));
    }
    return response;
  } catch (err) {
    dispatch(errorHandler(err as unknown as AxiosError));
  }
};

export const unLikeComment = (payload: TLikeCommentRequest) => async (dispatch: AppDispatch) => {
  try {
    const response: any =
      await new VentureAPIService().unlikeVentureVerse(payload);
    if (response.status === 200) {
      dispatch(unlikeCommentSuccess(payload.entityId));
    }
    return response;
  } catch (err) {
    dispatch(errorHandler(err as unknown as AxiosError));
  }
};

/*
 ** Replies
 */
export const replyToComment =
  (payload: TReplyToCommentRequest) => async (dispatch: AppDispatch) => {
    dispatch(updateReplyToCommentLoader(true));
    try {
      const { status }: any =
        await new VentureAPIService().replyToComment(payload);

      if (status === 200) {
        const payloadData: TGetVentureRepliesRequest = {
          userId: payload.userId,
          page: 1,
          limit: 5,
          commentId: payload.commentId
        };
        const response: any =
          await new VentureAPIService().getReplies(payloadData);
        let replies = []
        if (response.status === 200) {
          replies = response.data.data.replies
        }
        dispatch(replyToCommentSuccess({ commentId: payload.commentId, replies }));
      }
    } catch (err) {
      dispatch(errorHandler(err as unknown as AxiosError));
    } finally {
      dispatch(updateReplyToCommentLoader(false));
    }
  };

export const getReplies = (payload: TGetVentureRepliesRequest) => async (dispatch: AppDispatch) => {
  try {
    dispatch(startRepliesLoader());
    const { status, data }: any =
      await new VentureAPIService().getReplies(payload);

    if (status === 200) {
      dispatch(getRepliesSuccess(data?.data));
    }
  } catch (err) {
    console.log(err, 'errorDAta');
  } finally {
    dispatch(stopRepliesLoader());
  }
};

export const editReplyToComment =
  (payload: TEditReplyToCommentRequest) => async (dispatch: AppDispatch) => {
    try {
      dispatch(updateEditReplyToCommentLoader(true));
      const { status }: APIResponse<TReplyToCommentResponse> =
        await new VentureAPIService().editReplyToComment(payload);

      if (status === 200) {
        const { replyId, content } = payload;

        dispatch(editReplyToCommentSuccess({ replyId, text: content }));
      }
    } catch (err) {
      dispatch(errorHandler(err as unknown as AxiosError));
    } finally {
      dispatch(updateEditReplyToCommentLoader(false));
    }
  };
export const deleteReplyToComment =
  (payload: TDeleteReplyToCommentRequest) => async (dispatch: AppDispatch) => {
    try {
      const { status }: APIResponse<TDeleteReplyToCommentResponse> =
        await new VentureAPIService().deleteReplyToComment(payload);

      if (status === 200) {
        dispatch(deleteReplySuccess(payload));
      }
    } catch (err) {
      dispatch(errorHandler(err as unknown as AxiosError));
    }
  };

export const likeReply = (payload: TLikeCommentRequest) => async (dispatch: AppDispatch) => {
  try {
    const response: any = await new VentureAPIService().likeVentureVerse(payload);
    if (response.status === 200) {
      dispatch(likeReplySuccess({ replyId: payload.entityId }));
    }
    return response;
  } catch (err) {
    dispatch(errorHandler(err as unknown as AxiosError));
  }
};

export const unLikeReply = (payload: TLikeCommentRequest) => async (dispatch: AppDispatch) => {
  try {
    const response: any =
      await new VentureAPIService().unlikeVentureVerse(payload);
    if (response.status === 200) {
      dispatch(unlikeReplySuccess({ replyId: payload.entityId }));
    }
    return response;
  } catch (err) {
    dispatch(errorHandler(err as unknown as AxiosError));
  }
};

export const getDemoDay = (payload: { ventureVerseId: string }) => async (dispatch: AppDispatch) => {
  dispatch(startVentureLoading())
  try {
    const response: any =
      await new VentureAPIService().getDemoDay(payload);
    if (response.status === 200) {
      dispatch(demoDaySuccess(response?.data?.data))
    }
    return response;
  } catch (err) {
    if (err.response?.data?.status === '404') {
      dispatch(demoDaySuccess(null))
    }
    console.log(err, 'errorDataaaaaaaaaaa');
  } finally {
    dispatch(endVentureLoading())
  }
}

export const getGlobalOngoingDemoDay = () => async (dispatch: AppDispatch) => {
  try {
    const response: any =
      await new VentureAPIService().ongoingdemoday({});
    if (response?.data?.status && response?.data?.data) {
      dispatch(globalDemoDaySuccess(response?.data?.data))
    }
    return response;
  } catch (err) {
    return { status: false }
  }
}
export const paymentForDemoDay = (payload: Payment) => async (dispatch: AppDispatch) => {
  try {
    const response: any =
      await new VentureAPIService().payment(payload);
    return response;
  } catch (err) {
    return err
  }
};

export const paymentVerification = (payload: VerifyPayment) => async (dispatch: AppDispatch) => {
  try {
    const response: any =
      await new VentureAPIService().verifyOrder(payload);
    return response;
  } catch (err) {
    return err
  }
};

export const registerDemoDay = (payload: DemoDayBooking) => async (dispatch: AppDispatch) => {
  try {
    const response: any =
      await new VentureAPIService().registerDemoDay(payload);
    return response;
  } catch (err) {

  }
};

export const addPaymentDetailToDemoDay = (payload: any) => async (dispatch: AppDispatch) => {
  try {
    const response: any =
      await new VentureAPIService().paymentAdd(payload);
    return response;
  } catch (err) {
    return { status: false }
  }
};
